/**
 * Feature hook: Checkout / Undo Checkout document.
 * API → slice → hook → component with loading and result dialog (same pattern as useUploadToOneDrive).
 * Uses documentCheckoutFlowSlice for normal flow; legacy onCheckoutDocuments for retry/rollback.
 */
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import { onCheckoutDocuments } from "../slices/documentsAction";
import {
  performCheckoutThunk,
  closeCheckoutDialog,
} from "../slices/documents/documentCheckoutFlowSlice";
import { useTools } from "./useTools";
import {
  DATA_LOAD_STATUS,
  CHECKOUT_STATUS,
  CHECKOUT_TYPE,
  REFRESH_TOKEN_ACTION,
  RESPONSE_TYPE,
} from "../constants/metaData";
import { getDocClassCodeOnly } from "../../functions/helperMethod";

const DEFAULT_TOKEN_REFRESH_PRE_ACTION = {
  retry: 0,
  actionType: REFRESH_TOKEN_ACTION.documentCheckoutLoad,
  refreshResponse: RESPONSE_TYPE.none,
};

function buildCheckoutList(item) {
  console.log("Building checkout list from item", { item });
  const encodedTitle = encodeURIComponent(item.title ?? "");
  return [
    {
      class: getDocClassCodeOnly(item.class ?? item.docClass ?? ""),
      docNo: item.docNo,
      sheetNo: item.sheetNo,
      docRev: item.docRev,
      docType: item.docType,
      fileNo: item.fileNo,
      title: encodedTitle,
      fileExtension: item.fileExtension,
      state: item.state,
      fileState: item.fileState,
      owner: item.owner,
      lastActionDate: new Date().toISOString().split("T")[0],
      isDownloaded: false,
    },
  ];
}

export function useCheckout() {
  const dispatch = useAppDispatch();
  const checkoutState = useAppSelector((state) => state.CheckoutDocument);
  const flowState = useAppSelector((state) => state.DocumentCheckoutFlow);
  const { onIfsDocumentLoadChange } = useTools();

  const performCheckout = useCallback(
    (
      item,
      tokenRefreshPreAction = DEFAULT_TOKEN_REFRESH_PRE_ACTION,
      rollbackAction = false,
      uploadContext = null,
      flowOptions = null
    ) => {
      if (!item) return undefined;
      const checkoutList = buildCheckoutList(item);
      const isOneDriveDoc = item.isOneDriveDoc === true;
      const operationStatus =
        rollbackAction === true
          ? CHECKOUT_STATUS.undoCheckout
          : item.fileState === CHECKOUT_TYPE.checkout
            ? CHECKOUT_STATUS.undoCheckout
            : CHECKOUT_STATUS.checkout;

      const useLegacy =
        rollbackAction === true || (tokenRefreshPreAction?.retry ?? 0) > 0;

      onIfsDocumentLoadChange(DATA_LOAD_STATUS.refresh);

      if (useLegacy) {
        dispatch(
          onCheckoutDocuments(
            checkoutList,
            operationStatus,
            isOneDriveDoc,
            tokenRefreshPreAction,
            rollbackAction
          )
        );
        return undefined;
      }
      const toastOverrides =
        flowOptions?.suppressSuccessToast === true
          ? { __toast: { showSuccess: false } }
          : {};

      return dispatch(
        performCheckoutThunk({
          checkoutList,
          operationStatus,
          isOneDriveDoc,
          isRollback: rollbackAction,
          uploadContext: uploadContext ?? undefined,
          suppressCheckoutUi: flowOptions?.suppressCheckoutUi === true,
          progressMessage: flowOptions?.progressMessage,
          ...toastOverrides,
        })
      );
    },
    [dispatch, onIfsDocumentLoadChange]
  );

  const undoCheckoutDocument = useCallback(
    (item, tokenRefreshPreAction = DEFAULT_TOKEN_REFRESH_PRE_ACTION) => {
      performCheckout(item, tokenRefreshPreAction, true);
    },
    [performCheckout]
  );

  const getCheckoutButtonText = useCallback((item) => {
    if (!item || !item.fileState) return "Check-Out";
    return item.fileState === "Checked In" ? "Check-Out" : "Undo Check-Out";
  }, []);

  return {
    ...checkoutState,
    onCheckoutDocuments: (selectedIfsDocuments, operationStatus, isOneDriveDoc, tokenRefreshPreAction, isRollback) =>
      dispatch(
        onCheckoutDocuments(
          selectedIfsDocuments,
          operationStatus,
          isOneDriveDoc,
          tokenRefreshPreAction,
          isRollback
        )
      ),
    performCheckout,
    undoCheckoutDocument,
    getCheckoutButtonText,
    checkoutFlow: {
      loading: flowState?.loading ?? false,
      message: flowState?.message ?? "",
      isDialogOpen: flowState?.isDialogOpen ?? false,
      dialogVariant: flowState?.dialogVariant ?? "info",
      lastOperationWasCheckout: flowState?.lastOperationWasCheckout ?? false,
      closeCheckoutDialog: () => dispatch(closeCheckoutDialog()),
    },
  };
}
